import React, { useEffect, useRef, useState } from "react";
import { IMaskInput } from "react-imask";
import { getDictionaryItems } from "../../../../../../api/financeApi";
import { displayValue, formatAmount } from "../../mock/operationsJournalMock";
import "./CorrectionForm.css";

/** Поля стороны проводки; ключ в данных — field + Dt / Kt */
const FORM_FIELDS = [
  { field: "account", label: "Счёт" },
  { field: "articleOpiu", label: "Статья ОПИУ" },
  { field: "cfoOpiu", label: "ЦФО ОПИУ" },
  { field: "articleDds", label: "Статья ДДС" },
  { field: "cfoDds", label: "ЦФО ДДС" },
  { field: "project", label: "Проект" },
  { field: "department", label: "Отделение" },
  { field: "counterparty", label: "Контрагент" },
  { field: "employee", label: "Сотрудник" },
  { field: "contract", label: "Договор" },
  { field: "nomenclature", label: "Номенклатура" },
  { field: "fixedAsset", label: "Основное средство" },
  { field: "intangibleAsset", label: "Нематериальный актив" },
  { field: "cashAccount", label: "Денежный счёт" },
  { field: "workType", label: "Вид работ" },
];

const SIDES = [
  { id: "dt", suffix: "Dt", title: "Дебеты" },
  { id: "kt", suffix: "Kt", title: "Кредиты" },
];

const isEmpty = (value) => value == null || value === "";

function DictionaryInput({ dictionaryCode, fieldKey, value, onChange }) {
  const [options, setOptions] = useState([]);
  const listId = `ojcf-dict-${fieldKey}`;

  useEffect(() => {
    if (!dictionaryCode) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      getDictionaryItems(dictionaryCode, {
        q: value || undefined,
        limit: 50,
        signal: controller.signal,
      })
        .then((items) => setOptions(Array.isArray(items) ? items : []))
        .catch(() => {});
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [dictionaryCode, value]);

  return (
    <>
      <input
        className={`ojcf-control ojcf-control--input${isEmpty(value) ? "" : " is-changed"}`}
        list={dictionaryCode ? listId : undefined}
        value={value || ""}
        placeholder="Начните вводить..."
        onChange={(event) => onChange(event.target.value)}
      />
      {dictionaryCode && (
        <datalist id={listId}>
          {options.map((item) => (
            <option key={item.id || item.name} value={item.name}>
              {item.code && item.code !== item.name ? item.code : ""}
            </option>
          ))}
        </datalist>
      )}
    </>
  );
}

const cleanFields = (values) =>
  Object.fromEntries(Object.entries(values).filter(([, value]) => !isEmpty(value)));

const isSameFields = (a, b) => {
  const keysA = Object.keys(a);
  return (
    keysA.length === Object.keys(b).length &&
    keysA.every((key) => String(a[key]) === String(b[key]))
  );
};

function ArrowIcon() {
  return (
    <svg className="ojcf-row__arrow" width="14" height="10" viewBox="0 0 14 10" fill="none">
      <path
        d="M1 5h11M8.5 1.5L12 5 8.5 8.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AddFieldButton({ fields, onAdd }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="ojcf-add" ref={ref}>
      <button
        type="button"
        className="ojcf-add__button"
        disabled={!fields.length}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="ojcf-add__plus">+</span>
        Добавить поле
      </button>
      {isOpen && (
        <ul className="ojcf-add__menu">
          {fields.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => {
                  onAdd(item.key);
                  setIsOpen(false);
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Форма корректировки операции в карточке: исходное значение → новое значение.
 * Рендерит тело и футер панели, блок слоёв передаётся через layersControl.
 */
function CorrectionForm({
  operation,
  unit,
  editor,
  authorName,
  layersControl,
  dictionaryByField = {},
  onPersist,
  onCancel,
  onSaved,
}) {
  const [initialValues] = useState(() => editor.getEffectiveFields(operation.id));
  const [values, setValues] = useState(initialValues);
  const [addedKeys, setAddedKeys] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const cleaned = cleanFields(values);
  const hasChanges = !isSameFields(cleaned, cleanFields(initialValues));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (onPersist) await onPersist(operation.id, cleaned);
      editor.commitRow(operation.id, cleaned, authorName);
      onSaved();
    } catch (saveError) {
      setError(saveError.message || "Не удалось сохранить корректировку");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="ojd-body">
        {layersControl}

        {SIDES.map((side) => {
          const fields = FORM_FIELDS.map((item) => ({
            ...item,
            key: `${item.field}${side.suffix}`,
          }));
          const visible = fields.filter(
            (item) =>
              !isEmpty(operation[item.key]) ||
              !isEmpty(values[item.key]) ||
              addedKeys.includes(item.key)
          );
          const hidden = fields.filter((item) => !visible.includes(item));

          return (
            <section key={side.id} className="ojcf-card">
              <h3 className="ojcf-card__title">{side.title}</h3>
              {visible.map((item) => {
                const current = values[item.key] ?? "";
                return (
                  <div key={item.key} className="ojcf-row">
                    <span className="ojcf-row__label">{item.label}</span>
                    <span
                      className={`ojcf-row__value${
                        isEmpty(operation[item.key]) ? " is-empty" : ""
                      }`}
                    >
                      <span className="ojcf-row__text">
                        {displayValue(operation[item.key])}
                      </span>
                      <ArrowIcon />
                    </span>
                    <DictionaryInput
                      dictionaryCode={dictionaryByField[item.field]}
                      fieldKey={item.key}
                      value={current}
                      onChange={(next) => setValue(item.key, next)}
                    />
                  </div>
                );
              })}
              <AddFieldButton
                fields={hidden}
                onAdd={(key) => setAddedKeys((prev) => [...prev, key])}
              />
            </section>
          );
        })}

        <section className="ojcf-card">
          <div className="ojcf-row">
            <span className="ojcf-row__label">Сумма</span>
            <span className="ojcf-row__value">
              <span className="ojcf-row__text">
                {formatAmount(operation.amount, unit)}
              </span>
              <ArrowIcon />
            </span>
            <IMaskInput
              className={`ojcf-control ojcf-control--input${
                isEmpty(values.amount) ? "" : " is-changed"
              }`}
              mask={Number}
              scale={2}
              radix=","
              mapToRadix={["."]}
              thousandsSeparator=" "
              min={0}
              unmask
              value={isEmpty(values.amount) ? "" : String(values.amount)}
              onAccept={(value) => setValue("amount", value === "" ? null : Number(value))}
              placeholder="Новая сумма, ₸"
            />
          </div>
          <div className="ojcf-comment">
            <textarea
              className="ojcf-comment__input"
              rows={2}
              value={values.reason || ""}
              onChange={(event) => setValue("reason", event.target.value)}
              placeholder="Основание корректировки..."
            />
          </div>
          <div className="ojcf-comment">
            <textarea
              className="ojcf-comment__input"
              rows={3}
              value={values.correctionComment || ""}
              onChange={(event) => setValue("correctionComment", event.target.value)}
              placeholder="Комментарий к корректировке..."
            />
            <svg className="ojcf-comment__icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M8.5 2.5l3 3M1.5 12.5l.6-2.9 7.6-7.6a1.4 1.4 0 012 0l.8.8a1.4 1.4 0 010 2l-7.6 7.6-2.9.6z"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </section>
      </div>

      <footer className="ojd-footer ojcf-footer">
        <button type="button" className="ojcf-footer__cancel" onClick={onCancel}>
          Отмена
        </button>
        <button
          type="button"
          className="ojd-footer__primary"
          disabled={!hasChanges || saving}
          onClick={handleSave}
        >
          {saving ? "Сохранение..." : "Сохранить"}
        </button>
      </footer>
      {error && <div className="oj-export-error">{error}</div>}
    </>
  );
}

export default CorrectionForm;
